import { useTranslation } from 'next-i18next'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import Image from 'next/image'
import { handleLogout } from 'store/actions/auth.action'
import { useDispatch } from 'react-redux'
import { creditModal } from 'store/actions/header.action'
import { mainThemes } from 'utils/constants'
import { setTheme } from 'store/actions/theme.action'
import { useMediaQuery } from 'react-responsive'
import {
  LogOut,
  User,
  CreditCard,
  Key,
  LayoutDashboard,
  Shield,
  Plus,
  ArrowUpRight,
  Palette,
  Globe,
} from 'lucide-react'
import { useMemo } from 'react'

const links = [
  {
    title: 'account',
    icon: <User className="w-4 h-4 text-gray-700" />,
    to: '/account',
    onlyDesktop: 'true',
  },
  {
    title: 'dashboard',
    icon: <LayoutDashboard className="w-4 h-4 text-gray-700" />,
    to: '/dashboard',
  },
  {
    title: 'api-keys',
    icon: <Key className="w-4 h-4 text-gray-700" />,
    to: '/api-keys',
    onlyDesktop: 'true',
  },
  {
    title: 'billing',
    icon: <CreditCard className="w-4 h-4 text-gray-700" />,
    to: '/billing',
    onlyDesktop: 'true',
  },
  {
    title: 'privacy',
    icon: <Shield className="w-4 h-4 text-gray-700" />,
    to: '/privacy',
    onlyDesktop: 'true',
  },
]

const SettingDropdown = ({ onClick }) => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const { pathname } = useRouter()
  const router = useRouter()
  const { user, subscription } = useSelector((state) => state.auth)
  const { activeTheme } = useSelector((state) => state.theme)
  const isMobile = useMediaQuery({ maxWidth: 600 })

  const info = useMemo(
    () => [
      {
        title: 'role',
        value: 'Administrator',
        icon: <User className="w-4 h-4 text-gray-700" />,
      },
      {
        title: 'plan',
        value: subscription?.subscription_plans?.payment_name || 'Free',
        button: 'upgrade',
        icon: <CreditCard className="w-4 h-4 text-gray-700" />,
        handleClick: () => router.replace('/pricing'),
      },
      {
        title: 'credits',
        value: subscription?.credits,
        button: 'add',
        icon: <Plus className="w-4 h-4 text-gray-700" />,
        handleClick: () => dispatch(creditModal(true)),
      },
    ],
    [subscription, router, dispatch]
  )

  return (
    <div className="flex bg-white w-[280px]">
      <div className="flex flex-col w-full">
        {/* User Profile Section */}
        <div className="flex p-4 flex-col gap-3 items-center border-b border-solid border-zinc-200 dark:border-zinc-800">
          <div className="relative">
            {user?.avatar_url && (
              <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-white dark:ring-zinc-800 shadow-md">
                <Image
                  className="w-full object-cover h-full"
                  src={user?.avatar_url || '/placeholder.svg'}
                  width={64}
                  height={64}
                  alt="avatar"
                />
              </div>
            )}
            <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-800" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{user?.full_name}</p>
            <span className="text-sm text-zinc-500 dark:text-zinc-400 lowercase">{user?.email}</span>
          </div>
        </div>

        {/* User Info Section */}
        <div className="py-2 flex flex-col border-b border-solid border-zinc-200 dark:border-zinc-800">
          {info
            .filter((item) => item?.title != 'email')
            .map((item, i) => (
              <div
                className="flex justify-between px-4 py-2.5 items-center hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-200"
                key={i}
              >
                <div className="flex items-center gap-2">
                  {item.icon}
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {t(`account.${item.title}`)}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-zinc-500 dark:text-zinc-400">{item.value}</span>
                  {item.button && (
                    <button
                      className="hidden md:flex items-center gap-1 bg-black hover:bg-zinc-800 px-2.5 py-1 rounded-full text-xs font-medium text-white transition-colors duration-200"
                      onClick={item.handleClick}
                    >
                      {item.button === 'upgrade' && <ArrowUpRight className="w-3 h-3" />}
                      {item.button === 'add' && <Plus className="w-3 h-3" />}
                      {t(`account.${item.button}`)}
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>

        {/* Links Section */}
        <div className="py-2 flex flex-col border-b border-solid border-zinc-200 dark:border-zinc-800">
          {isMobile
            ? links.map((item, i) => (
                <NextLink
                  key={i}
                  href={item.to}
                  onClick={onClick}
                  className="flex justify-between px-4 py-2.5 items-center hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-200"
                >
                  <div className="flex gap-2 items-center">
                    {item.icon}
                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {t(`pages.${item.title}`)}
                    </span>
                  </div>
                </NextLink>
              ))
            : links
                .filter((item) => item.onlyDesktop)
                .map((item, i) => (
                  <NextLink
                    key={i}
                    href={item.to}
                    onClick={onClick}
                    className="flex justify-between px-4 py-2.5 items-center hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-200"
                  >
                    <div className="flex gap-2 items-center">
                      {item.icon}
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {t(`pages.${item.title}`)}
                      </span>
                    </div>
                  </NextLink>
                ))}
        </div>

        {/* Theme Selection */}
        <div className="flex gap-x-3 items-center px-4 py-3 border-b border-solid border-zinc-200 dark:border-zinc-800">
          <Palette className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          <div className="flex gap-3">
            {mainThemes.map((item, i) => (
              <div
                key={`th-${i}`}
                className={`cursor-pointer font-medium text-sm transition-all duration-200 ${
                  activeTheme == item.value
                    ? 'text-zinc-900 dark:text-zinc-100'
                    : 'text-zinc-400 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-400'
                }`}
                onClick={() => dispatch(setTheme(item.value))}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* Language Selection */}
        <div className="flex gap-x-3 items-center px-4 py-3 border-b border-solid border-zinc-200 dark:border-zinc-800">
          <Globe className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          <div className="flex gap-3">
            {['en', 'es', 'hi'].map((lang) => (
              <div
                key={`lang-${lang}`}
                className={`uppercase cursor-pointer font-medium text-sm transition-all duration-200 ${
                  router.locale == lang
                    ? 'text-zinc-900 dark:text-zinc-100'
                    : 'text-zinc-400 dark:text-zinc-600 hover:text-zinc-600 dark:hover:text-zinc-400'
                }`}
              >
                <NextLink href={router.asPath} locale={lang}>
                  {lang}
                </NextLink>
              </div>
            ))}
          </div>
        </div>

        {/* Logout Button */}
        <button
          className="flex items-center gap-2 p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors duration-200 w-full text-left"
          onClick={() => {
            dispatch(handleLogout(pathname, router))
          }}
        >
          <LogOut className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('auth.logout')}</div>
        </button>
      </div>
    </div>
  )
}

export default SettingDropdown
